import { Component, Input, ViewChild } from '@angular/core';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { isDate } from 'date-fns';
import { tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../../../common/take-until-destroy.decorator';
import { useDispatcher } from '../../../../../store.decorator';
import { focusItem } from '../../function/focus-item.function';
import { ViewTaskItem } from '../../model/view-task-item.model';
import { deleteItem } from '../../state/delete-item.action';
import { editItem } from '../../state/edit-item.action';
import { updateItem } from '../../state/update-item.action';
import { TaskListDialogService } from '../../task-list-dialog.service';
import { taskItemTransformer } from '../../transformer/task-item.transformer';
import { TaskPropsComponent } from '../task-props/task-props.component';
import { TaskTitleComponent } from '../task-title/task-title.component';

import { TranslationKeyMap } from './task.component.i18n';

@Component({
    selector: 'cmps-task-list-task',
    templateUrl: './task.component.html',
    host: {
        class: 'cmps-cdk-d-flex cmps-cdk-align-items-center',
        '[class.cmps-cdk-bg-color-white]': '_mouseoverTask !== true',
        '[class.cmps-cdk-bg-color-grey-200]':
            'item?.completed === true || _mouseoverTask === true',
        '(mouseenter)': '_mouseoverTask = true',
        '(mouseleave)': '_mouseoverTask = false',
    },
})
@TakeUntilDestroy()
export class TaskComponent {
    @Input() item: ViewTaskItem;
    @Input() dataContainerValue: string;

    /** @private */
    _mouseoverTask: boolean;

    @ViewChild(TaskTitleComponent)
    private readonly _titleRef: TaskTitleComponent;

    @ViewChild(TaskPropsComponent)
    private readonly _propsRef: TaskPropsComponent;

    constructor(
        private readonly _translateService: TranslateService,
        private readonly _dialog: CmpsDialog,
        /** @private */ readonly _service: TaskListDialogService,
    ) {}

    /** @private */
    _onClick(): void {
        const nextTask: ViewTaskItem = {
            ...this.item,
            completed: this.item.completed !== true,
        };
        this._update(nextTask);
    }

    /** @private */
    _onDueDateTimeChange(dueDateTime: Date | null): void {
        let next: Date | null;
        if (dueDateTime != null && !isDate(dueDateTime)) {
            next = this.item.dueDateTime == null ? null : this.item.dueDateTime;
        } else {
            next = !isNaN(dueDateTime?.getTime()) ? dueDateTime : null;
        }
        if (this.item.dueDateTime === next) {
            // revert the invalid date without going through the reducer
            this._propsRef?.dueDateTimeRef?.writeValue(this.item.dueDateTime);
            return;
        }
        const nextTask: ViewTaskItem = {
            ...this.item,
            dueDateTime: next,
        };
        this._update(nextTask);
    }

    /** @private */
    _onImportanceClick(): void {
        const nextTask: ViewTaskItem = {
            ...this.item,
            important: this.item.important !== true,
        };
        this._update(nextTask);
    }

    /** @private */
    _onDeleteClick(): void {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._11250_PLEASE_CONFIRM_YOU_WOULD_LIKE_TO_DELETE_THIS_TASK,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._121_CANCEL,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._11251_CONFIRM,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret !== 1) {
                        return;
                    }

                    useDispatcher(
                        this._service,
                        deleteItem({ msftTaskId: this.item.msftTaskId }),
                    );

                    void this._service.socketService.deleteTask(
                        this.item.msftTaskId,
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onEditClick(): void {
        useDispatcher(
            this._service,
            editItem({
                msftTaskId: this.item.msftTaskId,
                edit: true,
            }),
        );

        focusItem(
            `[data-container="${this.dataContainerValue}"] [data-task="${
                this.item.msftTaskId ?? 'unknown'
            }"]`,
        );
    }

    /** @private */
    _onSaveClick(): void {
        useDispatcher(
            this._service,
            editItem({
                msftTaskId: this.item.msftTaskId,
                edit: false,
            }),
        );

        const title = this._titleRef.nextTitle ?? this.item.title;
        if (title === this.item.title) {
            return;
        }

        const nextTask: ViewTaskItem = { ...this.item, title };
        this._update(nextTask);
    }

    /** @private */
    _onCancelEditClick(): void {
        useDispatcher(
            this._service,
            editItem({
                msftTaskId: this.item.msftTaskId,
                edit: false,
            }),
        );
    }

    private _update(nextTask: ViewTaskItem): void {
        useDispatcher(this._service, updateItem({ task: nextTask }));

        const item = taskItemTransformer([nextTask])[0];
        void this._service.socketService.updateTask(item);
    }
}
